import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Sparkles, BookOpen, HandHeart, Award, Bell, ChevronRight, ShieldCheck, UserPlus, Flame, Users, GraduationCap, Crown, MapPin, Compass, Building2, Music, Heart, Baby, Calendar } from "lucide-react";
import MobileShell from "../components/MobileShell";
import { NoorBackdrop } from "../components/NoorBackdrop";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [tasbih, setTasbih] = useState({ today: 0, streak: 0, total: 0 });
  const [memberships, setMemberships] = useState([]);
  const [journalCount, setJournalCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => { (async () => {
    try {
      const [t, m, j, a] = await Promise.all([
        api.get("/tasbih/state"),
        api.get("/memberships"),
        api.get("/journal"),
        api.get("/admin/me").catch(() => ({ data: { is_admin: false } })),
      ]);
      setTasbih(t.data); setMemberships(m.data.memberships || []); setJournalCount((j.data.entries || []).length);
      setIsAdmin(Boolean(a.data.is_admin));
    } catch (e) {}
  })(); }, []);

  const handleLogout = async () => { await logout(); window.location.href = "/login"; };
  const name = user?.name || "Friend";
  const initial = name.charAt(0).toUpperCase();

  return (
    <MobileShell>
      <div className="relative" data-testid="profile-page">
        <NoorBackdrop />
        <header className="flex items-start justify-between px-5 pt-9">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-deep/45">Profile</p>
            <h1 className="mt-1 font-display text-2xl text-deep" data-testid="profile-name">{name}</h1>
          </div>
        </header>

        <section className="mt-5 px-5">
          <div className="glass shadow-elegant rounded-3xl p-5" data-testid="profile-card">
            <div className="flex items-center gap-4">
              <div className="bg-gold-gradient noor-ring text-deep flex h-16 w-16 items-center justify-center rounded-full font-display text-2xl">
                {initial}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-deep">{user?.city || "Add your city"}</p>
                <p className="text-xs text-deep/55">
                  {user?.status === "member" ? "Member · access unlocked" : "Explorer"}
                  {user?.invites_available !== undefined && ` · ${user.invites_available} invites left`}
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <Metric value={String(tasbih.streak ?? 0)} label="Streak" />
              <Metric value={String(tasbih.total ?? 0)} label="Dhikr" />
              <Metric value={String(journalCount)} label="Entries" />
            </div>
          </div>
        </section>

        <section className="mt-6 px-5">
          <h2 className="mb-3 font-display text-base text-deep">Badges</h2>
          <div className="-mx-5 flex gap-3 overflow-x-auto no-scrollbar px-5 pb-1">
            {[
              { icon: Sparkles, label: "Noor Seeker", sub: tasbih.streak >= 7 ? "Unlocked" : "Reflect 7d" },
              { icon: BookOpen, label: "Journalist", sub: journalCount >= 1 ? "Unlocked" : "First entry" },
              { icon: HandHeart, label: "Giver", sub: "Coming soon" },
              { icon: Award, label: "Mentor", sub: "Coming soon" },
            ].map((b) => (
              <div key={b.label} className="glass min-w-[112px] rounded-2xl p-3 text-center shadow-soft">
                <div className="bg-gold-gradient mx-auto flex h-12 w-12 items-center justify-center rounded-full">
                  <b.icon className="h-5 w-5 text-deep" />
                </div>
                <p className="mt-2 text-[11px] font-medium text-deep">{b.label}</p>
                <p className="text-[10px] text-deep/55">{b.sub}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 space-y-2 px-5">
          <RowLink to="/calendar" icon={Calendar} title="Ismaili Calendar" sub="Hijri dates, Navroz, Imamat Day, Salgirah & more" test="row-calendar" />
          <RowLink to="/sangat" icon={Compass} title="My Sangat" sub="Your spiritual passport — cities, mentors, khidmah" test="row-sangat" />
          <RowLink to="/ginan" icon={Music} title="Ginan" sub="Devotional poetry — Pir Hasan Kabirdin, Sadardin, Shams" test="row-ginan" />
          <RowLink to="/family" icon={Baby} title="Family Corner" sub="Tiny parenting prompts by age stage" test="row-family" />
          <RowLink to="/orgs" icon={Building2} title="Organisations" sub="Directory · become an organisation" test="row-orgs" />
          {isAdmin && (
            <RowLink to="/admin" icon={ShieldCheck} title="Admin · Stewards" sub="Verify organisations · steward the calm" test="row-admin" />
          )}
          <RowLink to="/invites" icon={UserPlus} title="Invitations" sub="Welcome friends with grace" test="row-invites" />
          <RowLink to="/jamatkhana" icon={MapPin} title="Find your Jamatkhana" sub="Auto-locate or browse by country" test="row-jamatkhana" />
          <RowLink to="/notifications" icon={Bell} title="Notifications" sub="Mentorship updates, mod actions, recognition" test="row-notifications" />
          <RowLink to="/khidmah" icon={Crown} title="Khidmah leaderboard" sub="Soft recognition of service this month" test="row-khidmah" />
          <RowLink to="/mentors" icon={GraduationCap} title="Mentorship" sub="Browse mentors · manage requests" test="row-mentors" />
          <RowLink to="/journal" icon={BookOpen} title="My journal" sub={`${journalCount} private entries`} test="row-journal" />
          <RowLink to="/tasbih" icon={Flame} title="Dhikr & streaks" sub={`${tasbih.streak ?? 0}-day streak`} test="row-tasbih" />
          <RowLink to="/reminders" icon={Bell} title="Reminders & Noor Nudges" sub="Soft in-app cues for prayer, dhikr & journaling" test="row-reminders" />
          <RowLink to="/moderation" icon={ShieldCheck} title="Moderation queue" sub="Moderators & admins — keep the circle calm" test="row-moderation" />
        </section>

        {memberships.length > 0 && (
          <section className="mt-6 px-5">
            <h2 className="mb-3 font-display text-base text-deep">Communities</h2>
            <div className="space-y-2.5">
              {memberships.map((m) => (
                <div key={m.community_id} className="glass flex items-center gap-3 rounded-2xl p-3.5 shadow-soft">
                  <div className="bg-emerald-gradient flex h-9 w-9 items-center justify-center rounded-full">
                    <Users className="h-4 w-4 text-gold" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-deep">{m.community?.name}</p>
                    <p className="text-[11px] text-deep/55">{m.role}</p>
                  </div>
                  <span className="text-[11px] text-deep/75">Active</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-6 px-5 pb-6">
          <p className="mb-3 px-2 text-[10px] leading-relaxed text-deep/45">
            Tasbih.ai is an independent community platform — not a religious authority, fatwa platform, or institutional representative.
          </p>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="glass flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-medium text-deep shadow-soft tap-scale"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
          <div className="mt-5 flex items-center justify-center gap-4 text-[11px] text-deep/55">
            <Link to="/privacy" data-testid="footer-privacy" className="tap-scale">Privacy</Link>
            <span className="text-deep/20">·</span>
            <Link to="/terms" data-testid="footer-terms" className="tap-scale">Terms</Link>
            <span className="text-deep/20">·</span>
            <a href="mailto:hello@tasbih.ai" className="tap-scale">Contact</a>
          </div>
          <p className="mt-3 px-2 text-center text-[10px] leading-relaxed text-deep/40" data-testid="profile-credit">
            Content curated with care by <strong className="text-deep/55">Naushad & Shabnam Patel</strong><br />
            Andheri Jamatkhana · Mumbai · India
          </p>
        </section>
      </div>
    </MobileShell>
  );
}

function Metric({ value, label }) {
  return (
    <div className="rounded-2xl bg-sand/60 p-3">
      <p className="font-display text-xl text-deep">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-deep/55">{label}</p>
    </div>
  );
}

function RowLink({ to, icon: Icon, title, sub, test }) {
  return (
    <Link to={to} data-testid={test} className="glass tap-scale flex items-center gap-3 rounded-2xl p-4 shadow-soft">
      <div className="bg-gold-gradient flex h-10 w-10 items-center justify-center rounded-full">
        <Icon className="h-4 w-4 text-deep" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-deep">{title}</p>
        <p className="text-[11px] text-deep/55">{sub}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-deep/45" />
    </Link>
  );
}

function Row({ icon: Icon, title, sub }) {
  return (
    <div className="glass flex items-center gap-3 rounded-2xl p-4 shadow-soft">
      <div className="bg-gold-gradient flex h-10 w-10 items-center justify-center rounded-full">
        <Icon className="h-4 w-4 text-deep" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-deep">{title}</p>
        <p className="text-[11px] text-deep/55">{sub}</p>
      </div>
    </div>
  );
}
